/**
 * Demo DB の型定義のみを持つモジュール。
 *
 * 元は `schema.ts` に DemoDatabase クラス（Dexie + EntityTable<T> × 25 + migration version × 16）
 * と全ての型定義（700 行超）が同居していたため、route -api / -query / components が
 * `import type { Foo } from "@/mocks/db/schema"` するだけで Dexie generic Table 型 + 全 EntityTable
 * の instantiation を tsgo に評価させ、型相関 Map を圧迫していた。
 *
 * このモジュールは型のみを export し、DemoDatabase クラスは見えない。
 * 値（`db`）が必要な mock repository 層だけが `schema.ts` を import する設計に寄せる。
 */

import type { StructureCellRole } from '@/features/engine-shared/structure-detail/types';

import type { OperationEventSnapshotRow } from './operationEventPayload';

// ─── Existing ───────────────────────────────────────────────

export interface Person {
  id: string;
  name: string;
  department: string;
}

// ─── Schema-local row type (decoupled from feature UI types) ─

export interface SchemaTableColumn {
  largeCategoryCode: string;
  largeCategoryName: string;
  mediumCategoryCode: string;
  mediumCategoryName: string;
  inputFieldCount: number;
  requiredFieldCount: number;
  hasIncludeFile: boolean;
}

export interface SchemaTableTarget {
  targetType: string;
  targetId: string;
  targetName: string;
  targetAvatarUrl?: string;
}

export interface SchemaTableRow {
  id: string;
  completedOperationStepCount: number;
  totalOperationStepCount: number;
  target: SchemaTableTarget;
  anchorDate: string;
  columns: SchemaTableColumn[];
}

// ─── Attendance Domain ──────────────────────────────────────

export type AttendanceRecordStatus =
  | 'present'
  | 'absent'
  | 'late'
  | 'early_leave'
  | 'holiday'
  | 'paid_leave'
  | 'sick_leave'
  | 'non_working_day';

export type AttendanceApprovalStatus = 'pending' | 'approved' | 'rejected';

export type ClockingRecordType =
  | 'clock_in'
  | 'clock_out'
  | 'break_start'
  | 'break_end';

export type ClockingRecordMethod =
  | 'web'
  | 'mobile'
  | 'ic_card'
  | 'biometric'
  | 'manual';

export type AttendanceAlertType =
  | 'overtime_limit'
  | 'no_clock_in'
  | 'no_clock_out'
  | 'consecutive_overtime'
  | 'interval_violation'
  | 'monthly_80h_exceeded'
  | 'annual_360h_exceeded';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export type AttendanceEmployeeStatus = 'active' | 'on_leave' | 'retired';

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  status: AttendanceRecordStatus;
  overtimeMinutes: number;
  lateMinutes: number;
  memo: string;
  workplaceName: string;
  approvalStatus: AttendanceApprovalStatus;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClockingRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  timestamp: string;
  type: ClockingRecordType;
  method: ClockingRecordMethod;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  deviceId: string | null;
  verified: boolean;
  organizationId: string;
  createdAt: string;
}

export interface AttendanceAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: AttendanceAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}

export interface AttendanceEmployee {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  attendanceStructureId: string;
  attendanceStructureName: string;
  attendanceManagerId: string | null;
  attendanceManagerName: string | null;
  monthlyWorkDays: number;
  monthlyOvertimeHours: number;
  paidLeaveRemaining: number;
  paidLeaveTotal: number;
  status: AttendanceEmployeeStatus;
  organizationId: string;
}

// ─── Payroll Domain ─────────────────────────────────────────

export type PaySlipStatus =
  | 'draft'
  | 'calculating'
  | 'pending_approval'
  | 'approved'
  | 'paid'
  | 'cancelled';

export type PaySlipRecordCategory =
  | 'earning'
  | 'deduction'
  | 'tax'
  | 'social_insurance'
  | 'net_pay';

export interface PaySlip {
  id: string;
  title: string;
  period: string;
  paymentDate: string;
  status: PaySlipStatus;
  employeeCount: number;
  totalGrossAmount: number;
  totalNetAmount: number;
  totalDeductions: number;
  currency: string;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaySlipRecord {
  id: string;
  paySlipId: string;
  employeeId: string;
  employeeName: string;
  department: string;
  category: PaySlipRecordCategory;
  itemName: string;
  itemCode: string;
  amount: number;
  currency: string;
  description: string;
  sortOrder: number;
  createdAt: string;
}

export interface PaySlipFolder {
  id: string;
  paySlipId: string;
  name: string;
  description: string;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaySlipFile {
  id: string;
  paySlipId: string;
  folderId: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedBy: string;
  createdAt: string;
}

export interface EmployeePaySlipSummary {
  id: string;
  employeeId: string;
  employeeName: string;
  email: string;
  avatarUrl: string | null;
  organization: string;
  period: string;
  grossAmount: number;
  prevGrossAmount: number | null;
  totalDeductions: number;
  prevTotalDeductions: number | null;
  netAmount: number;
  prevNetAmount: number | null;
  overtimeHours: string;
  operationActivity: string | null;
  organizationId: string;
}

export interface EmployeePayslipAttendance {
  id: string;
  employeeId: string;
  period: string;
  organizationId: string;
  scheduledWorkDays: number;
  attendanceDays: number;
  absenceDays: number;
  holidayWorkDays: number;
  paidLeaveUsedDays: number;
  paidLeaveRemainingDays: number;
  workHours: number;
  overtimeHours: number;
  nightOvertimeHours: number;
  holidayWorkHours: number;
  lateEarlyHours: number;
}

export type PaySlipCalculationCategoryId =
  | 'attendance'
  | 'earnings'
  | 'social_insurance'
  | 'tax'
  | 'result';

export interface PaySlipPayItemLine {
  lineKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
}

export interface PaySlipCalculationSnapshotRow {
  categoryId: PaySlipCalculationCategoryId;
  variableKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
  lines?: PaySlipPayItemLine[] | null;
}

export interface PaySlipCalculationSnapshot {
  id: string;
  employeeId: string;
  period: string;
  snapshotAt: string;
  title: string;
  rows: PaySlipCalculationSnapshotRow[];
  organizationId: string;
}

// ─── Engine Domain ──────────────────────────────────────────

export type EngineLifecycleStatus = 'draft' | 'applied' | 'archived';

/** Logic component の表示用 glyph 種別 (LogicComponentCard の見た目を決める)。 */
export type LogicComponentGlyphKind =
  | 'branch'
  | 'tableLookup'
  | 'split'
  | 'rounding'
  | 'sum'
  | 'arithmetic'
  | 'lookup'
  | 'switch';

/** Payroll item の type — 給与明細上のロール。
 *  earning: 支給項目 / deduction: 控除項目。
 *  入力データ・マスタ・中間値・帳票出力等は未設定 (undefined)。 */
export type PayrollItemType = 'earning' | 'deduction';

/** Payroll item の category — 制定根拠。
 *  statutory: 法定（健保・年金・税等） / company: 会社独自（役職手当等）。 */
export type PayrollItemCategory = 'statutory' | 'company';

/** Payroll item の variability — 月次変動性。
 *  fixed: 月毎で変動しない / variable: 月毎で変動する。 */
export type PayrollItemVariability = 'fixed' | 'variable';

/** Payroll item の taxability — 課税対象性。
 *  taxable: 課税 / non-taxable: 非課税 /
 *  partially-taxable: 一部課税（限度額超過分のみ課税の通勤費等） /
 *  conditionally-taxable: 条件付き課税（条件で課税範囲が変わる）。 */
export type PayrollItemTaxability =
  | 'taxable'
  | 'non-taxable'
  | 'partially-taxable'
  | 'conditionally-taxable';

/** Payroll engine の item 原子マスタ。
 *  「年俸額」「健康保険料」「標準報酬月額」のような payroll structure / unit から参照される atom。
 *  category / kind union は持たない (engineComponents 時代の遺物を排除)。 */
export interface PayrollItem {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  /** JSON.stringify された任意の設定。tablePreview もここに含める。 */
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** structure detail カードの badge 文字 (例: "標報", "健保", "年俸") */
  displayBadge?: string;
  /** structure detail カードの badge 種別ラベル (例: "整数", "DEDUCTION・STATUTORY・VARIABLE") */
  displayBadgeKind?: string;
  /** 管理用名称 (例: name = 「基本給」, managementName = 「管理職基本給」)。 */
  managementName?: string;
  /** 給与明細上の表示名 (`name` が制度上の正式名で、明細では別名で出すケース)。 */
  paySlipDisplayName?: string;
  /** 給与明細上のロール。入力データやマスタ等の非明細項目は undefined。 */
  type?: PayrollItemType;
  /** 制定根拠。`type` 未設定の item では undefined。 */
  category?: PayrollItemCategory;
  /** 月次変動性。`type` 未設定の item では undefined。 */
  variability?: PayrollItemVariability;
  /** 課税対象性。`type === "earning"` の item で意味を持つ。 */
  taxability?: PayrollItemTaxability;
  /** 遡及計算対象か（前月以前への確定後再計算の対象）。 */
  retroactive?: boolean;
  /** 給与明細上の表示順 (昇順)。明細項目でない item は undefined。 */
  paySlipOrder?: number;
  /** Numbering CSV に基づく 4 桁番号。CSV にマッチしない item は undefined。 */
  number?: number;
}

/** Payroll engine の logic component 原子マスタ。
 *  「料率テーブル Lookup」「労使折半」「端数処理」「× 12 月割」のような計算ロジック atom。 */
export interface PayrollLogicComponent {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadgeKind?: string;
  /** LogicComponentCard の glyph (枠の中の絵柄) を決める。 */
  glyphKind?: LogicComponentGlyphKind;
}

/** Attendance engine の item 原子マスタ。PayrollItem と同形。 */
export interface AttendanceItem {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadge?: string;
  displayBadgeKind?: string;
}

/** Attendance engine の logic component 原子マスタ。PayrollLogicComponent と同形。 */
export interface AttendanceLogicComponent {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadgeKind?: string;
  glyphKind?: LogicComponentGlyphKind;
}

export interface AttendanceStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する AttendanceItem の id */
  itemIds: string[];
  /** この structure が直接参照する AttendanceLogicComponent の id */
  logicComponentIds: string[];
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する PayrollItem の id */
  itemIds: string[];
  /** この structure が直接参照する PayrollLogicComponent の id */
  logicComponentIds: string[];
  compositionStages?: string[][];
  /** PayrollLogicComponent の id (= payment cycle として扱う logic component) */
  paymentCycleId?: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Unit / UnitItem / UnitLogicComponent (catalog atoms, structure 非従属) ─────────

// 型は features/engine-shared/structure-detail/types.ts が所有。mocks 側は consumer。
export type {
  StructureCardBadgeTone,
  StructureCellRole,
  StructureTablePreview,
} from '@/features/engine-shared/structure-detail/types';

/** PayrollUnit はカタログ atom として独立に存在する。Structure は unitIds で参照する。 */
export interface PayrollUnit {
  id: string;
  name: string;
  description?: string;
  parentUnitId?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  /** サイドタブで上位カテゴリ見出しを差し込むためのキー。Structure 側で同じ key を持つ unit が連続する区間に subheader を表示する。未設定の Unit は subheader を出さない（後方互換）。 */
  categoryKey?: string;
  lifecycleStatus: EngineLifecycleStatus;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Numbering CSV に基づく単一番号。CSV と直接マッチしない unit は undefined。 */
  number?: number;
}

/** PayrollUnit の input / output に登場する item の join 行。
 *  表示用情報は itemId → PayrollItem を join して取得する。 */
export interface PayrollUnitItem {
  id: string;
  unitId: string;
  /** FK → payrollItems.id。branch label 用 unit item (True/False output) は省略可。 */
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  /** subject/period 依存の mock 表示値 ("472,460" 等)。未指定なら空欄。 */
  valueOverride?: string;
  /** 分岐 unit の True/False output 用。itemId と排他。 */
  branchLabel?: 'T' | 'F';
  /** 分岐先 unit の名前 (例: "介護保険料計算")。 */
  branchTargetUnitName?: string;
}

/** PayrollUnit の process に登場する logic component の join 行。
 *  表示用情報は logicComponentId → PayrollLogicComponent を join して取得する。 */
export interface PayrollUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  例: subtract の場合は被減数と減数の itemId。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}

export interface AttendanceUnit {
  id: string;
  name: string;
  description?: string;
  parentUnitId?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  lifecycleStatus: EngineLifecycleStatus;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceUnitItem {
  id: string;
  unitId: string;
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  valueOverride?: string;
  branchLabel?: 'T' | 'F';
  branchTargetUnitName?: string;
}

export interface AttendanceUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}

// ─── Core Domain ────────────────────────────────────────────

export type PayrollAttendanceMetricType =
  | 'employee_count'
  | 'payroll_status'
  | 'attendance_rate'
  | 'overtime_hours'
  | 'alert_count'
  | 'processing_progress';

export type MetricTrend = 'up' | 'down' | 'stable';

export type OperationPackageType =
  | 'payroll'
  | 'attendance'
  | 'payroll_attendance';

export type OperationPackageCategory =
  | 'monthly_close'
  | 'year_end'
  | 'onboarding'
  | 'offboarding'
  | 'mid_year_adjustment'
  | 'bonus_processing';

export type OperationPackageStatus = 'template' | 'in_progress' | 'completed';

export interface PayrollAttendanceDashboardMetric {
  id: string;
  period: string;
  metricType: PayrollAttendanceMetricType;
  label: string;
  value: number;
  unit: string;
  trend: MetricTrend;
  trendPercent: number;
  organizationId: string;
  updatedAt: string;
}

export interface OperationPackage {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  type: OperationPackageType;
  category: OperationPackageCategory;
  steps: string;
  estimatedDuration: string;
  assigneeId: string | null;
  status: OperationPackageStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}

export interface AttendanceOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}

export interface AttendanceOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}

export interface PayrollOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}

export interface PayrollOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}

export interface PayrollOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}

// ─── Attendance Rule & Policy ───────────────────────────────

export type AttendanceRulePolicyCategory =
  | 'assignment'
  | 'attendance'
  | 'period';

export interface AttendanceRulePolicy {
  id: string;
  category: AttendanceRulePolicyCategory;
  name: string;
  description: string;
  mandatory: boolean;
  clauseTypes: string[];
  config: string;
  organizationId: string;
  updatedAt: string;
}

// ─── Payroll Alert ──────────────────────────────────────────

export type PayrollAlertCategory =
  | 'calculation'
  | 'compliance'
  | 'payment'
  | 'deadline';

export type PayrollAlertType =
  | 'calc_failed'
  | 'negative_net_pay'
  | 'deduction_exceeds_gross'
  | 'minimum_wage_violation'
  | 'overtime_rate_mismatch'
  | 'withholding_tax_anomaly'
  | 'missing_bank_account'
  | 'fb_data_mismatch'
  | 'payment_date_overdue'
  | 'closing_deadline_exceeded'
  | 'resident_tax_update_due'
  | 'year_end_adjustment_pending';

export interface PayrollAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  period: string;
  payGroup: string;
  category: PayrollAlertCategory;
  type: PayrollAlertType;
  severity: AlertSeverity;
  message: string;
  detectedAt: string;
  resolved: boolean;
  resolvedAt: string | null;
  referenceId: string | null;
  organizationId: string;
}

// ─── Employee Pay Slip Alerts (detail page) ─────────────────
// payroll alerts と同形だが、pay slip 詳細画面の alerts タブ専用に独立テーブルで保持する。
// 既存 payrollAlerts への絞り込みは行わず、employee 単位で seed する。

export type EmployeePaySlipAlertCategory = PayrollAlertCategory;
export type EmployeePaySlipAlertType = PayrollAlertType;

export interface EmployeePaySlipAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  period: string;
  payGroup: string;
  category: EmployeePaySlipAlertCategory;
  type: EmployeePaySlipAlertType;
  severity: AlertSeverity;
  message: string;
  detectedAt: string;
  resolved: boolean;
  resolvedAt: string | null;
  referenceId: string | null;
  organizationId: string;
}

// ─── Employee Clocking Record Alerts (detail page) ──────────
// attendance alerts と同形だが、clocking record 詳細画面の alerts タブ専用に独立テーブルで保持する。

export type EmployeeClockingRecordAlertType = AttendanceAlertType;

export interface EmployeeClockingRecordAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: EmployeeClockingRecordAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}

// ─── Employee Pay Slip / Clocking Record Files (detail page) ─
// device files の簡易版。folder type 単位でファイル一覧を保持する mock 専用テーブル。

export type EmployeePaySlipFileFolderType =
  | 'payslip'
  | 'bonus'
  | 'yearEndAdjustment';

export type EmployeeClockingRecordFileFolderType =
  | 'attendanceBook'
  | 'shiftTable'
  | 'leaveRequest';

export interface EmployeePaySlipDetailFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeePaySlipFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}

export interface EmployeeClockingRecordFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeeClockingRecordFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}

// ─── Payroll Overview Facts ─────────────────────────────────

export type PayrollOverviewOfficeKey =
  | 'tokyo_hq'
  | 'sendai'
  | 'fukuoka'
  | 'osaka'
  | 'sapporo';

export interface PayrollOverviewFact {
  id: string;
  employeeId: string;
  period: string;
  organizationId: string;
  officeKey: PayrollOverviewOfficeKey;
  baseSalary: number;
  overtimePay: number;
  allowances: number;
  grossPay: number;
  incomeTax: number;
  residentTax: number;
  healthInsurance: number;
  pensionInsurance: number;
  employmentInsurance: number;
  otherDeductions: number;
  totalDeductions: number;
  netPay: number;
  employerSocialInsuranceBurden: number;
}

// ─── Attendance Operation Activity ──────────────────────────

export type AttendanceActivityCategory =
  | 'clocking'
  | 'request'
  | 'alert'
  | 'policy';

export type AttendanceActivityType =
  | 'clocking.created'
  | 'clocking.edited'
  | 'clocking.deleted'
  | 'clocking.approved'
  | 'clocking.rejected'
  | 'request.leaveSubmitted'
  | 'request.overtimeSubmitted'
  | 'request.holidayWorkSubmitted'
  | 'request.approved'
  | 'request.rejected'
  | 'request.cancelled'
  | 'alert.triggered'
  | 'alert.acknowledged'
  | 'alert.resolved'
  | 'policy.ruleUpdated'
  | 'policy.attendanceStructureAssigned'
  | 'shift.assigned'
  | 'attendance.monthlyClosed'
  | 'attendance.reopened';

export type ActivityActorKind = 'user' | 'system';

export type ActivityOriginKind = 'manual' | 'operation';

export interface AttendanceOperationActivity {
  id: string;
  correlationId: string;
  occurredAt: string;
  category: AttendanceActivityCategory;
  activityType: AttendanceActivityType;
  targetEmployeeId: string;
  targetEmployeeName: string;
  actorId: string | null;
  actorName: string | null;
  actorKind: ActivityActorKind;
  originKind: ActivityOriginKind;
  operationTaskName: string | null;
  operationPackageName: string | null;
  patternName: string | null;
  detail: string;
  payload: string;
  organizationId: string;
}

// ─── Individual Portal Mock (visual-only dashboard) ─────────

export interface IndividualPortalAttendance {
  id: string;
  /** ISO date string for the displayed day (e.g. "2025-03-28"). */
  date: string;
  /** When true, the "打刻漏れ" alert row is shown. */
  hasMissingPunch: boolean;
}

export interface IndividualPortalTask {
  id: string;
  name: string;
  /** ISO date string. */
  dueDate: string;
  completedSteps: number;
  totalSteps: number;
  /** Sort order for stable rendering. */
  sortOrder: number;
  /**
   * When set, clicking the task in the portal opens the sequence runner at
   * `/individual/portal/sequence/{sequenceInstanceId}`. Leave undefined for
   * static / non-interactive task rows.
   */
  sequenceInstanceId?: string;
}

// ─── Individual Portal Sequence (AI-driven flow) ────────────

export type IndividualPortalSequenceKind =
  | 'year_end_adjustment'
  | 'onboarding'
  | 'hire_event';

/** Speaker identity rendered above a chat bubble when multiple parties are present. */
export interface SequenceChatIdentity {
  name: string;
  avatarUrl?: string;
  avatarFallback?: string;
}

/** A pre-baked chat history entry shown above the floating chat card. */
export interface SequenceChatHistoryItem {
  key: string;
  kind:
    | 'ai_bubble'
    | 'user_summary_card'
    | 'user_file_card'
    | 'draft_link'
    | 'tool_call'
    | 'automap_link';
  /** AI bubble text — supports `<strong>` and `<br/>` placeholder tokens. */
  text?: string;
  /** Title for summary cards ("STEP 1 回答"). */
  title?: string;
  /** Rows for summary cards. */
  rows?: { label: string; value: string }[];
  /** File entries for `user_file_card`. */
  files?: { name: string }[];
  /** Optional speaker identity. Falls back to default by `kind` when omitted. */
  identity?: SequenceChatIdentity;
  /** ISO datetime string. Optional footer display below the bubble. */
  timestamp?: string;
  /**
   * Optional direction override. By default user_summary_card / user_file_card は outgoing、
   * ai_bubble は incoming に表示される。
   * 例: 他者から転送された情報を `user_summary_card` で表示するときは "incoming" を指定する。
   */
  direction?: 'incoming' | 'outgoing';
  /** Label for the inline link button on `draft_link` / `automap_link`. */
  linkLabel?: string;
  /**
   * Tool name shown on `tool_call` chips (例: "Automap Ingester")。
   * 直近の pending tool_call item に対しては、reveal 前の thinking インジケーターが
   * "Calling ${toolName}..." と表示される。
   */
  toolName?: string;
}

export type SequenceAnswerSpec =
  | {
      kind: 'yes_no';
      yesLabel: string;
      noLabel: string;
      /**
       * YES/NO の上に表示する補助情報 callout（例: 病院予約連絡先 / 招待メールの宛先・件名）。任意。
       * iconName は seed が指定する抽象識別子で、renderer 側で IconName に解決する。
       * rows が 1 件のときは prominent な大きい表示、複数件のときは label/value ペアを縦に並べる。
       */
      callout?: {
        iconName: string;
        rows: Array<{ label: string; value: string }>;
      };
    }
  | {
      kind: 'single_choice';
      options: Array<{
        key: string;
        pill?: string;
        label: string;
        sub?: string;
      }>;
    }
  | {
      kind: 'single_choice_with_specs';
      submitLabel: string;
      options: Array<{
        key: string;
        pill?: string;
        label: string;
        sub?: string;
        specs: { label: string; value: string }[];
      }>;
    }
  | {
      kind: 'multi_choice';
      defaultSelected?: string[];
      submitLabel: string;
      options: Array<{ key: string; label: string; sub?: string }>;
    }
  | {
      kind: 'amount';
      label: string;
      placeholder: string;
      suffix: string;
      submitLabel: string;
      quickValues: { value: string; label: string }[];
      format: 'yen';
    }
  | {
      kind: 'address_form';
      submitLabel: string;
      fields: Array<{
        id: string;
        label?: string;
        placeholder: string;
        defaultValue?: string;
        kind: 'postal' | 'city' | 'street' | 'tel' | 'text' | 'email';
      }>;
    }
  | {
      kind: 'text_input';
      label?: string;
      placeholder: string;
      defaultValue?: string;
      submitLabel: string;
      maxLength?: number;
      /** 任意質問の場合に「回答しない」ボタンを表示するためのラベル。未指定なら skip 不可。 */
      skipLabel?: string;
    }
  | {
      kind: 'date_input';
      label?: string;
      defaultValue?: string;
      submitLabel: string;
      min?: string;
      max?: string;
    }
  | {
      kind: 'field_confirm';
      rows: Array<{ id: string; label: string; value: string }>;
      yesLabel: string;
      noLabel: string;
    }
  | {
      kind: 'upload';
      hint: string;
      skipOption?: { key: string; pill?: string; label: string; sub?: string };
      manualOption?: {
        key: string;
        pill?: string;
        label: string;
        sub?: string;
      };
      acceptedMimeTypes: string[];
      maxSizeBytes: number;
    }
  | {
      kind: 'automap_review';
      title: string;
      subtitle: string;
      /** Upload step whose previewDataUrl + file names are surfaced on the left pane. */
      uploadStepId: string;
      certificates: AutomapCertificate[];
      confirmLabel: string;
    }
  | { kind: 'complete'; ctaLabel: string };

export interface SequenceStepDefinition {
  id: string;
  order: number;
  /** Right-panel + sidebar label, e.g. "個人情報・扶養家族の確認". */
  navLabel: string;
  /** Floating chat card title. */
  title: string;
  /** Floating chat card subtitle. */
  subtitle?: string;
  /** Pre-baked AI history shown above the floating card on entry. */
  history: SequenceChatHistoryItem[];
  answer: SequenceAnswerSpec;
}

export interface SequenceStackingDoc {
  date: string;
  iconName: string;
  label: string;
  /**
   * Reference エントリをクリックしたときに開くパネル。
   * 指定すると label がボタンになり、対応するハンドラが呼ばれる。
   */
  action?: 'automap';
  /**
   * このエントリを表示し始める step id。指定された step に到達するまでは
   * stackingDocs から隠す。未指定なら最初から表示される。
   * 例: "ye-step-automap" を指定すると、抽出 step の chat やりとりが
   *     発生して以降のみレファレンスに現れる。
   */
  visibleAfterStepId?: string;
}

export type DraftBadgeTone =
  | 'primary'
  | 'success'
  | 'warning'
  | 'neutral'
  | 'draft';

export type DraftDcBlock =
  | {
      kind: 'field_group';
      title: string;
      cols: 1 | 2;
      fields: { label: string; value: string }[];
    }
  | {
      kind: 'deduction_table';
      title?: string;
      rows: {
        provider: string;
        type: string;
        period?: string;
        amount: string;
      }[];
    }
  | {
      kind: 'section_divider';
      iconName?: string;
      label: string;
      rightBadge?: { text: string; tone: DraftBadgeTone };
    }
  | { kind: 'info_callout'; iconName: string; text: string }
  | {
      kind: 'summary_table';
      title?: string;
      headerLabels: { type: string; details: string; amount: string };
      rows: { type: string; provider: string; amount: string }[];
      totalRowLabel: string;
      totalAmount: string;
    }
  | {
      kind: 'doc_table';
      rows: {
        name: string;
        fileName: string;
        status: { label: string; tone: DraftBadgeTone };
      }[];
    };

export interface DraftDcContent {
  badge: string;
  title: string;
  subtitle: string;
  blocks: DraftDcBlock[];
}

export interface SequenceDetailDraft {
  kind: IndividualPortalSequenceKind;
  draftDc: DraftDcContent;
  draftPlain: string;
}

/**
 * AI mock sequence のサイド。
 * - "individual": 従業員（被管理者）が起点となるフロー（従来から存在）
 * - "cockpit": 人事労務担当者（管理者）が後続業務を実行するフロー
 *
 * `IndividualPortal*` という型名は歴史的経緯（individual 側から先に実装された）。
 * 現在は両サイドの sequence / chat session を単一の Dexie テーブルで保持している。
 */
export type AiMockSide = 'individual' | 'cockpit';

/** v5 以前のレコードは `side` を持たないため、既定値 "individual" として扱う。 */
export function rowSide(row: { side?: AiMockSide }): AiMockSide {
  return row.side ?? 'individual';
}

export interface IndividualPortalSequenceDefinition {
  id: string;
  kind: IndividualPortalSequenceKind;
  /** どちらのサイドのフローか。未指定の場合は "individual" として扱う。 */
  side?: AiMockSide;
  packageTitle: string;
  shortName: string;
  estimatedMinutes: number;
  steps: SequenceStepDefinition[];
  stackingDocs: SequenceStackingDoc[];
  detailDraft: SequenceDetailDraft;
  /** 詳細パネル下部の確定ボタンのラベル。未指定なら「申請する」。業務処理系では「確定する」など。 */
  submitLabel?: string;
  /** 「申請する」押下後に staged reveal で流す履歴。完了確認 + 受理メッセージ。 */
  completionHistory?: SequenceChatHistoryItem[];
}

export type IndividualPortalSequenceInstanceStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed';

export type IndividualPortalSequenceRightPanelMode =
  | 'cards'
  | 'detail'
  | 'automap';

export interface IndividualPortalSequenceInstance {
  id: string;
  definitionId: string;
  taskId: string;
  status: IndividualPortalSequenceInstanceStatus;
  currentStepId: string;
  rightPanelMode: IndividualPortalSequenceRightPanelMode;
  sidebarOpen: boolean;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export type SequenceUploadedFile = {
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  receivedAt: string;
  /**
   * Image-only inline preview. Populated by the mock upload API via
   * FileReader.readAsDataURL so the Automap review can render the original
   * source after reload. Absent for PDFs and other non-image uploads.
   */
  previewDataUrl?: string;
  /**
   * Runtime-only blob URL (created via URL.createObjectURL).
   * Used to preview real uploaded files (PDF iframe etc.) in the Automap
   * Ingester review screen. Not persisted across reloads.
   */
  objectUrl?: string;
};

export type AutomapFieldConfidence = 'high' | 'medium' | 'low';

export interface AutomapField {
  id: string;
  label: string;
  defaultValue: string;
  kind: 'text' | 'amount';
  confidence: AutomapFieldConfidence;
}

export interface AutomapCertificate {
  id: string;
  documentLabel: string;
  fields: AutomapField[];
}

export type SequenceStepRecordAnswer =
  | { kind: 'yes_no'; value: 'yes' | 'no' }
  | { kind: 'single_choice'; selectedKey: string }
  | { kind: 'single_choice_with_specs'; selectedKey: string }
  | { kind: 'multi_choice'; selectedKeys: string[] }
  | { kind: 'amount'; value: string }
  | { kind: 'address_form'; values: Record<string, string> }
  | { kind: 'field_confirm'; value: 'yes' | 'no' }
  | { kind: 'text_input'; value: string }
  | { kind: 'date_input'; value: string }
  | {
      kind: 'upload';
      files: SequenceUploadedFile[];
      skipped?: boolean;
    }
  | {
      kind: 'automap_review';
      values: Array<{
        certificateId: string;
        fields: Array<{ id: string; value: string }>;
      }>;
    }
  | { kind: 'complete' };

export interface IndividualPortalSequenceStepRecord {
  /** Composite: `${instanceId}:${stepId}` */
  id: string;
  instanceId: string;
  stepId: string;
  status: 'pending' | 'in_progress' | 'completed';
  completedAt: string | null;
  answer: SequenceStepRecordAnswer | null;
}

export type IndividualPortalApprovalStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'rejected';

export type IndividualPortalApprovalCategory =
  | 'labor'
  | 'it'
  | 'attendance'
  | 'ga'
  | 'contract';

export interface IndividualPortalApprovalFlowStep {
  order: number;
  approverName: string;
  approverAvatarColor: string;
  status: 'completed' | 'current' | 'pending';
  /** Set on the step that the current viewer is responsible for. */
  isMe?: boolean;
}

export interface IndividualPortalApprovalFormField {
  label: string;
  value: string;
}

export interface IndividualPortalApproval {
  id: string;
  taskName: string;
  /** ISO date string. */
  approvalDeadline: string;
  /** ISO date string. */
  submittedAt: string;
  requesterName: string;
  /** Avatar accent color used for the round chip behind the requester name. */
  requesterAvatarColor: string;
  approvalFlowLabel: string;
  status: IndividualPortalApprovalStatus;
  category: IndividualPortalApprovalCategory;
  /** Form key/value summary shown in the drawer body. */
  formSummary: IndividualPortalApprovalFormField[];
  /** Approval-flow steps shown in the drawer body. */
  approvalFlow: IndividualPortalApprovalFlowStep[];
  sortOrder: number;
}

export type IndividualPortalRequestApp = 'zoom' | 'zendesk';

export type IndividualPortalRequestStatus = 'in_progress' | 'rejected';

export interface IndividualPortalRequest {
  id: string;
  name: string;
  app: IndividualPortalRequestApp;
  /** ISO date string. */
  submittedAt: string;
  currentStep: number;
  totalSteps: number;
  status: IndividualPortalRequestStatus;
  sortOrder: number;
}

export type IndividualPortalRequestFormCategory =
  | 'attendance'
  | 'it'
  | 'labor'
  | 'contract';

export interface IndividualPortalRequestForm {
  id: string;
  name: string;
  category: IndividualPortalRequestFormCategory;
  sortOrder: number;
  /**
   * BE の OperationPackageType に対応。値が設定されている手続きは Individual から申請可能で、
   * /v2/self-service/operation-packages の packageType と一致したパッケージへ遷移できる。
   */
  packageType?: string;
}

export interface IndividualPortalAiChatSession {
  id: string;
  title: string;
  subtitle: string;
  /** ISO datetime. */
  lastInteractedAt: string;
  /** Linked sequence instance id. `null` for non-clickable items. */
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドのセッションか。未指定の場合は "individual"。 */
  side?: AiMockSide;
}

export interface IndividualPortalUpcomingInteraction {
  id: string;
  title: string;
  subtitle: string;
  /** ISO date. */
  scheduledAt: string;
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドの予定か。未指定の場合は "individual"。 */
  side?: AiMockSide;
}

// ─── Global AI Chat Mock ─────────────────────────────────────
// セッション・メッセージは BE（oRPC chatContract）が SSOT のためモック型を持たない。
// モデル選択のみ BE に概念が無い FE 専用機能としてモックする。

// ─── UI Preferences (frontend-only, key-value) ──────────────

export interface UiPreference {
  key: string;
  value: unknown;
}

// ─── Pay slip PDF mock (single common blob for "PDFでみる" button) ─

export interface PaySlipPdfMock {
  id: string;
  blob: Blob;
}

// ─── Re-export gantt snapshot row from operationEventPayload ─

export type { OperationEventSnapshotRow };
